/**
 * AndromedaScore — computes a 0-100 score for a carousel based on Meta-Andromeda
 * best practices (slide-1 hook strength, story arc, bullet list, CTA, headline
 * length, slide count, brand consistency, identity-lock).
 *
 * Returns:
 *   { score: number, breakdown: [{ key, label, points, max, ok, tip? }] }
 */

export const computeAndromedaScore = ({
  slides = [],
  brandColors = [],
  selectedPersonId,
  hasLogoConfigured,
}) => {
  const breakdown = [];

  // 1) Slide-1 (cover) hook strength — number / question / strong painpoint
  const slide1 = slides[0];
  const slide1Text = ((slide1?.headline || "") + " " + (slide1?.sub_text || "")).toLowerCase();
  let hookPoints = 0;
  if (/\d+[%€]|\d+\s?(prozent|euro|jahre|tage|wochen|monate|jobs|stellen)/.test(slide1Text)) {
    hookPoints = 20; // has a number → strongest stat-hook
  } else if (slide1Text.includes("?") || /^(was|wie|warum|weisst|wusstest|kennst|kennen sie|wer)/.test(slide1?.headline?.toLowerCase() || "")) {
    hookPoints = 15;
  } else if (slide1?.headline && slide1.headline.length > 0) {
    hookPoints = 10;
  }
  breakdown.push({
    key: "hook",
    label: "Starker Cover-Hook",
    points: hookPoints,
    max: 20,
    ok: hookPoints >= 15,
    tip: hookPoints < 15 ? "Cover-Slide mit einer Zahl/Statistik oder provokanten Frage stärken." : null,
  });

  // 2) Story arc: cover → story → list/cta sequence
  const hasCover = slides.some((s, i) => i === 0 || s.slide_type === "cover");
  const hasStory = slides.some((s) => s.slide_type === "story");
  const hasCta = slides.some((s) => s.slide_type === "cta");
  let arcPoints = 0;
  if (hasCover) arcPoints += 5;
  if (hasStory) arcPoints += 5;
  if (hasCta) arcPoints += 5;
  breakdown.push({
    key: "arc",
    label: "Story-Arc komplett (Hook → Story → CTA)",
    points: arcPoints,
    max: 15,
    ok: arcPoints === 15,
    tip:
      arcPoints < 15
        ? `Fehlend: ${[!hasCover && "Cover", !hasStory && "Story-Slides", !hasCta && "CTA"].filter(Boolean).join(", ")}.`
        : null,
  });

  // 3) Bullet-Liste mit ≥ 3 Punkten
  const listSlide = slides.find((s) => s.slide_type === "list" && Array.isArray(s.bullet_points) && s.bullet_points.length >= 3);
  const listPoints = listSlide ? 15 : 0;
  breakdown.push({
    key: "list",
    label: "Bullet-Liste mit 3+ Punkten",
    points: listPoints,
    max: 15,
    ok: !!listSlide,
    tip: !listSlide ? "Eine Slide mit 3-5 ✅-Bullet-Punkten erhöht die Conversion signifikant." : null,
  });

  // 4) Anzahl Slides: 5-7 ideal
  let countPoints = 5;
  if (slides.length >= 5 && slides.length <= 7) countPoints = 10;
  else if (slides.length >= 4 && slides.length <= 8) countPoints = 7;
  breakdown.push({
    key: "count",
    label: `Slide-Anzahl (${slides.length})`,
    points: countPoints,
    max: 10,
    ok: countPoints >= 10,
    tip: countPoints < 10 ? "Andromeda performt am besten mit 5-7 Slides." : null,
  });

  // 5) Headline-Länge: alle ≤ 9 Wörter
  const longHeadlines = slides.filter((s) => (s.headline || "").trim().split(/\s+/).length > 9);
  let lengthPoints = 10;
  if (longHeadlines.length > 0) lengthPoints = Math.max(0, 10 - longHeadlines.length * 3);
  breakdown.push({
    key: "headline-length",
    label: "Kurze Headlines (≤9 Wörter)",
    points: lengthPoints,
    max: 10,
    ok: longHeadlines.length === 0,
    tip:
      longHeadlines.length > 0
        ? `${longHeadlines.length} Slide(s) haben zu lange Headlines. Maximal 9 Wörter.`
        : null,
  });

  // 6) Eindeutige Headlines (keine Duplikate)
  const headlinesLc = slides.map((s) => (s.headline || "").trim().toLowerCase());
  const uniqueCount = new Set(headlinesLc.filter(Boolean)).size;
  const dupPoints = uniqueCount === headlinesLc.filter(Boolean).length ? 10 : 5;
  breakdown.push({
    key: "unique-headlines",
    label: "Eindeutige Headlines",
    points: dupPoints,
    max: 10,
    ok: dupPoints === 10,
    tip: dupPoints < 10 ? "Doppelte Headlines werden von Andromeda abgewertet." : null,
  });

  // 7) Brand-Farben gesetzt (≥2)
  const colorCount = (brandColors || []).filter((c) => c && c.length >= 4).length;
  const colorPoints = colorCount >= 2 ? 10 : colorCount === 1 ? 5 : 0;
  breakdown.push({
    key: "colors",
    label: "Markenfarben (mind. 2)",
    points: colorPoints,
    max: 10,
    ok: colorCount >= 2,
    tip: colorCount < 2 ? "Mindestens 2 Markenfarben definieren für visuelle Konsistenz." : null,
  });

  // 8) Identity-Lock: Person ausgewählt
  const personPoints = selectedPersonId ? 10 : 0;
  breakdown.push({
    key: "person",
    label: "Person über alle Slides (Identity-Lock)",
    points: personPoints,
    max: 10,
    ok: !!selectedPersonId,
    tip: !selectedPersonId ? "Eine Person über alle Slides erhöht visuelle Konsistenz." : null,
  });

  const score = breakdown.reduce((sum, b) => sum + b.points, 0);
  return { score, breakdown };
};

export const getScoreColor = (score) => {
  if (score >= 85) return { color: "text-green-600 dark:text-green-400", bg: "bg-green-500/10", border: "border-green-500/30", label: "Optimal" };
  if (score >= 65) return { color: "text-yellow-600 dark:text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/30", label: "Gut" };
  if (score >= 40) return { color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30", label: "Ausbaufähig" };
  return { color: "text-red-600 dark:text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", label: "Schwach" };
};
